import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Toolbar } from "@/components/ui/toolbar/Toolbar";
import { Button } from "@/components/ui/button/Button";
import SelectDropdown from "@/components/ui/select-dropdown";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faGrid2Plus } from "@awesome.me/kit-70c1f0055f/icons/classic/light";
import TimePeriodSelector, { type Period } from "@/components/TimePeriodSelector";
import type { ExtendedAssetInfo } from "../hooks/useAssetManager";
import type { ChartSettings } from "../hooks/usePlaygroundSettings";
import { mapPeriodToApiTimeRange } from "../periodConfig";
import { getCorrelationMatrix } from "@/services/api/widget";
import type { Resource, Frequency } from "@/services/api/widget/types";
import { LoadingIndicator } from "@/components/chat";
import {
  downloadDelimited,
  type ExportFormat,
} from "../utils/downloadCSV";

interface CorrelationMatrixAreaProps {
  selectedAssets: string[];
  assetDisplayNames: Record<string, string>;
  assetMetadata: Record<string, ExtendedAssetInfo>;
  chartSettings: Partial<ChartSettings>;
  periods: Period[];
  isLoading: boolean;
  isInitialized?: boolean;
  onPeriodChange: (period: string) => void;
  onCustomDateChange: (range: [string, string]) => void;
  onAddToDashboard?: () => void;
}

const FREQUENCY_OPTIONS = [
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
];

// Color scale - green for high positive, amber for medium, red for negative
const getCorrelationColor = (value: number | null, isDiagonal: boolean): string => {
  if (value === null) return "bg-slate-700";
  
  // Clamp value between -1 and 1
  const v = Math.max(-1, Math.min(1, value));
  
  // Diagonal (self-correlation of 1.0)
  if (isDiagonal) return "bg-emerald-600";
  
  // High positive correlation (0.7 - 1.0)
  if (v >= 0.9) return "bg-emerald-500";
  if (v >= 0.7) return "bg-emerald-400";
  
  // Medium positive correlation (0.4 - 0.7)
  if (v >= 0.5) return "bg-amber-400";
  if (v >= 0.3) return "bg-amber-300";
  
  // Low positive correlation (0 - 0.4)
  if (v >= 0.1) return "bg-slate-400";
  if (v >= 0) return "bg-slate-500";
  
  // Negative correlation
  if (v >= -0.3) return "bg-orange-400";
  if (v >= -0.5) return "bg-orange-500";
  if (v >= -0.7) return "bg-red-500";
  return "bg-red-600";
};

const getTextColor = (value: number | null, isDiagonal: boolean): string => {
  if (value === null) return "text-slate-400";
  if (isDiagonal) return "text-white";
  
  const v = Math.abs(value);
  if (v >= 0.5) return "text-white";
  if (v >= 0.3) return "text-slate-900";
  return "text-white";
};

export const CorrelationMatrixArea: React.FC<CorrelationMatrixAreaProps> = ({
  selectedAssets,
  assetDisplayNames,
  assetMetadata,
  chartSettings,
  periods,
  isLoading: externalLoading,
  isInitialized = false,
  onPeriodChange,
  onCustomDateChange,
  onAddToDashboard,
}) => {
  const [matrix, setMatrix] = useState<number[][] | null>(null);
  const [resources, setResources] = useState<string[]>([]);
  const [resourceNames, setResourceNames] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [frequency, setFrequency] = useState<Frequency>("monthly");

  // Calculate time range from chart settings
  const timeRange = useMemo(
    () =>
      mapPeriodToApiTimeRange(
        chartSettings.period,
        chartSettings.customDateRange,
      ),
    [chartSettings.period, chartSettings.customDateRange],
  );

  // Fetch correlation matrix
  const fetchData = useCallback(async () => {
    if (!selectedAssets.length || selectedAssets.length < 2) {
      setMatrix(null);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Build resources list
      const resourcesList: Resource[] = selectedAssets.map((id) => {
        const meta = assetMetadata[id];
        return {
          resource_type: (meta?.resourceType as "product" | "portfolio" | "benchmark") || "product",
          resource_id: meta?.resourceId || id,
          stream_type: "default",
        };
      });

      const response = await getCorrelationMatrix({
        resources: resourcesList,
        time_range: timeRange,
        frequency,
      });

      setMatrix(response.matrix);
      setResources(response.resources);
      
      // Build resource names mapping - prefer our local display names over API names
      const names: Record<string, string> = {};
      response.resources.forEach((resourceKey) => {
        // Parse the resource key (format: "product:prod-123")
        const parts = resourceKey.split(":");
        const resourceType = parts[0];
        const resourceId = parts[1];
        
        // First try to find by resourceId in our display names (most direct)
        if (resourceId && assetDisplayNames[resourceId]) {
          names[resourceKey] = assetDisplayNames[resourceId];
          return;
        }
        
        // Try to find matching asset in our metadata
        for (const [assetId, meta] of Object.entries(assetMetadata)) {
          if (meta.resourceId === resourceId && meta.resourceType === resourceType) {
            names[resourceKey] = meta.displayName || assetDisplayNames[assetId] || resourceId;
            return;
          }
        }
        
        // Try matching by the full key
        if (assetDisplayNames[resourceKey]) {
          names[resourceKey] = assetDisplayNames[resourceKey];
          return;
        }
        
        // Fallback to API provided name or resource ID
        names[resourceKey] = response.resource_names?.[resourceKey] || resourceId || resourceKey;
      });
      
      setResourceNames(names);
    } catch (err) {
      console.error("Error fetching correlation matrix:", err);
      setError(err instanceof Error ? err.message : "Failed to fetch data");
    } finally {
      setIsLoading(false);
    }
  }, [selectedAssets, assetMetadata, assetDisplayNames, timeRange, frequency]);

  // Fetch data when dependencies change
  useEffect(() => {
    if (isInitialized) {
      fetchData();
    }
  }, [fetchData, isInitialized]);

  // Get display name for a resource
  const getDisplayName = useCallback(
    (resourceKey: string): string => {
      // Extract resource ID from key (e.g., "portfolio:port-xxx" -> "port-xxx")
      const resourceId = resourceKey.split(":")[1];
      
      // First try frontend asset display names (authoritative source from asset selection)
      if (resourceId && assetDisplayNames[resourceId]) {
        return assetDisplayNames[resourceId];
      }
      // Then try the backend response's resource names
      if (resourceNames[resourceKey]) {
        return resourceNames[resourceKey];
      }
      // Fall back to resource ID
      return resourceId || resourceKey;
    },
    [resourceNames, assetDisplayNames, assetMetadata]
  );

  /**
   * Export correlation matrix as CSV or TSV
   * Matrix format with asset names as row/column headers
   *
   * @param format - Export format (csv or tsv)
   */
  const handleExport = useCallback(
    (format: ExportFormat = "csv") => {
      if (!matrix || !resources.length) return;

      // Build column definitions - first column is the row header (asset name)
      const cols = [
        { id: "asset", label: "" },
        ...resources.map((resource) => ({
          id: resource,
          label: getDisplayName(resource),
        })),
      ];

      // Build row data - each row is an asset with correlation values to all other assets
      const rows = matrix.map((row, i) => {
        const rowData: Record<string, unknown> = {
          asset: getDisplayName(resources[i]),
        };

        resources.forEach((resource, j) => {
          rowData[resource] = row[j] !== null ? row[j] : "N/A";
        });

        return rowData;
      });

      const extension = format === "csv" ? "csv" : "tsv";
      downloadDelimited(rows, cols, `correlation_matrix.${extension}`, {
        mode: "correlation",
        format,
      });
    },
    [matrix, resources, getDisplayName]
  );

  const showLoading = isLoading || externalLoading;

  return (
    <div className="flex flex-col h-full">
      {/* Toolbar */}
      <Toolbar>
        <div className="flex items-center space-x-4">
          <TimePeriodSelector
            periods={periods}
            period={chartSettings.period || "3y"}
            setPeriod={onPeriodChange}
            customDateRange={chartSettings.customDateRange}
            setCustomDateRange={onCustomDateChange}
          />
          <div className="flex items-center space-x-2">
            <span className="text-sm text-slate-400">Frequency:</span>
            <SelectDropdown
              options={FREQUENCY_OPTIONS}
              value={frequency}
              onChange={(v) => setFrequency(v as Frequency)}
              className="w-36"
            />
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <Button
            color="color"
            className="px-2 py-1 min-h-0 border-none shadow-none"
            onClick={() => handleExport("csv")}
            disabled={!matrix}
            dropdown={true}
            dropdownItems={[
              {
                name: "Export CSV",
                onClick: () => handleExport("csv"),
              },
              {
                name: "Export TSV",
                onClick: () => handleExport("tsv"),
              },
            ]}
          >
            Export
          </Button>
          {onAddToDashboard && (
            <Button
              color="color"
              className="px-2 py-1 min-h-0 border-none shadow-none"
              onClick={onAddToDashboard}
              disabled={!matrix}
            >
              <FontAwesomeIcon icon={faGrid2Plus} className="h-4 w-4" />
            </Button>
          )}
        </div>
      </Toolbar>

      {/* Matrix Display */}
      <div className="flex-1 overflow-auto">
        {showLoading ? (
          <div className="flex items-center justify-center h-full">
            <LoadingIndicator />
          </div>
        ) : error ? (
          <div className="flex items-center justify-center h-full text-red-400">
            {error}
          </div>
        ) : !matrix || resources.length < 2 ? (
          <div className="flex items-center justify-center h-full text-slate-400">
            <div className="text-center">
              <p>Select at least 2 assets to view correlation matrix</p>
              <p className="text-sm mt-1">
                {selectedAssets.length} asset{selectedAssets.length !== 1 ? "s" : ""} selected
              </p>
            </div>
          </div>
        ) : (
          <div className="p-4">
            {/* Legend */}
            <div className="flex items-center gap-6 mb-4 text-sm text-slate-400">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-emerald-500 rounded" />
                <span>High (&gt;0.7)</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-amber-400 rounded" />
                <span>Medium (0.3-0.7)</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-slate-500 rounded" />
                <span>Low (0-0.3)</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-red-500 rounded" />
                <span>Negative</span>
              </div>
            </div>

            {/* Matrix Table */}
            <div className="overflow-x-auto">
              <table className="border-collapse">
                <thead>
                  <tr>
                    <th className="p-2 text-left text-slate-400 text-sm font-medium min-w-[150px]" />
                    {resources.map((resource) => (
                      <th
                        key={resource}
                        className="p-2 text-center text-slate-300 text-sm font-medium min-w-[120px] max-w-[120px]"
                      >
                        <div className="truncate" title={getDisplayName(resource)}>
                          {getDisplayName(resource)}
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {matrix.map((row, i) => (
                    <tr key={resources[i]}>
                      <td className="p-2 text-left text-slate-300 text-sm font-medium">
                        <div className="truncate " title={getDisplayName(resources[i])}>
                          {getDisplayName(resources[i])}
                        </div>
                      </td>
                      {row.map((value, j) => {
                        const isDiagonal = i === j;
                        return (
                          <td
                            key={`${i}-${j}`}
                            className={`p-2 text-center text-sm font-mono ${getCorrelationColor(value, isDiagonal)} ${getTextColor(value, isDiagonal)}`}
                            title={value !== null ? value.toFixed(4) : "N/A"}
                          >
                            {value !== null ? value.toFixed(2) : "—"}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

